import React, { useMemo } from 'react';
import { Badge, Tooltip, Loader, Alert, Stack, Group, Text, Button } from '@mantine/core';
import { IconShieldCheck, IconAlertCircle, IconRefresh } from '@tabler/icons-react';

/**
 * Renders the LLM-as-a-judge advisory grade for an AgentStudio artifact
 * (single agent JSON or flow JSON). Composes three pieces of UI:
 *
 *   1. A header badge (green ≥0.8 / yellow ≥0.6 / red <0.6) — meant to sit
 *      next to the existing model + duration badges in the result panel.
 *   2. A "Regenerate addressing N findings" button — only renders when there
 *      are non-info findings to address. Caller wires onRegenerate to the
 *      regeneration handler (re-submits with priorJudgeFindings spread in).
 *   3. A collapsible Alert with the per-item findings — caller decides where
 *      it goes (typically directly under the existing "Applied standards"
 *      row).
 *
 * Exposed separately as `<HeaderBadge />` and `<FindingsAlert />` (named
 * exports) so callers can split the layout. The default export renders both
 * stacked inside one Stack for simple result panels.
 *
 * Props:
 *   evaluating       — boolean; true while the judge is in flight (header
 *                      badge swaps to a loader)
 *   qualityScore     — number 0..1 (or null/undefined when no judge yet)
 *   qualityFindings  — JudgeFinding[] (perItem from the judge response)
 *   onRegenerate(findings) — optional callback; without it the regenerate
 *                            button is suppressed
 *   color            — Mantine color name (defaults to cyan to match the
 *                      AgentStudio palette)
 */

export function HeaderBadge({ evaluating, qualityScore, color = 'cyan' }) {
  if (evaluating) {
    return (
      <Badge variant="outline" color="gray" leftSection={<Loader size={10} />}>
        evaluating quality…
      </Badge>
    );
  }
  if (typeof qualityScore !== 'number' || Number.isNaN(qualityScore)) return null;
  const pct = Math.round(qualityScore * 100);
  const badgeColor = qualityScore >= 0.8 ? 'green' : qualityScore >= 0.6 ? 'yellow' : 'red';
  return (
    <Tooltip label="LLM-as-a-judge advisory grade. Drops automatically when the judge surfaces real concerns.">
      <Badge variant="filled" color={badgeColor} leftSection={<IconShieldCheck size={12} />}>
        quality: {pct}%
      </Badge>
    </Tooltip>
  );
}

export function RegenerateButton({ qualityFindings, onRegenerate, color = 'cyan' }) {
  const regenerateFindings = useMemo(
    () => (qualityFindings || []).filter((f) => f && f.severity !== 'info'),
    [qualityFindings]
  );
  if (!onRegenerate || regenerateFindings.length === 0) return null;
  return (
    <Tooltip label="Re-run the generator with the judge's findings appended to the prompt.">
      <Button
        variant="light"
        color="yellow"
        leftSection={<IconRefresh size={14} />}
        onClick={() => onRegenerate(regenerateFindings)}
      >
        Regenerate addressing {regenerateFindings.length} finding{regenerateFindings.length === 1 ? '' : 's'}
      </Button>
    </Tooltip>
  );
}

export function FindingsAlert({ qualityFindings, color = 'cyan' }) {
  const findings = Array.isArray(qualityFindings) ? qualityFindings : [];
  if (findings.length === 0) return null;
  // Worst severity drives the alert color so a single error-level finding
  // can't get camouflaged inside a yellow alert.
  const worst = findings.reduce(
    (acc, f) =>
      f.severity === 'error'
        ? 'error'
        : f.severity === 'warn' && acc !== 'error'
        ? 'warn'
        : acc,
    'info'
  );
  const alertColor = worst === 'error' ? 'red' : worst === 'warn' ? 'yellow' : 'blue';
  return (
    <Alert
      color={alertColor}
      icon={<IconAlertCircle size={16} />}
      title={`${findings.length} judge finding${findings.length === 1 ? '' : 's'}`}
      mb="sm"
      variant="light"
    >
      <Stack gap={4}>
        {findings.map((f, i) => (
          <Group key={i} gap={6} wrap="nowrap" align="flex-start">
            <Badge
              size="xs"
              variant="filled"
              color={f.severity === 'error' ? 'red' : f.severity === 'warn' ? 'yellow' : 'gray'}
            >
              {f.severity || 'info'}
            </Badge>
            <Text size="xs" style={{ flex: 1, minWidth: 0 }}>
              <strong>{f.itemId}:</strong> {f.reason}
              {f.suggestion ? ` — ${f.suggestion}` : ''}
            </Text>
          </Group>
        ))}
      </Stack>
    </Alert>
  );
}

function AgentQualityBadge(props) {
  return (
    <Stack gap="xs">
      <Group gap="xs" wrap="wrap">
        <HeaderBadge {...props} />
        <RegenerateButton {...props} />
      </Group>
      <FindingsAlert {...props} />
    </Stack>
  );
}

export default AgentQualityBadge;
